import { useEffect, useRef } from 'react';
import { useIsSSR } from '@react-aria/ssr';
import { useParams } from 'react-router';
import * as amplitudePlugin from '~/domain/plugins/services/amplitude.client';
import * as cookieConsentPlugin from '~/domain/plugins/services/cookie-consent';
import { CookieConsentRouteHandler } from '~/domain/plugins/services/cookie-consent';
import * as dash0Plugin from '~/domain/plugins/services/dash0.client';
import { bridgeMetaPixelCookies } from '~/domain/plugins/services/meta-pixel-bridge.client';

import * as trackerPlugin from '~/domain/plugins/services/tracker.client';
import { PageviewTracker } from '~/domain/plugins/services/tracker.client';
import type { PluginInitContext } from '~/domain/plugins/types';
import { setPluginsReady } from '~/domain/plugins/plugins-ready';
import type { MarketingProviders } from '~/domain/plugins/marketing-providers.server';

type PluginsProviderProps = {
  companyId: string;
  storefrontId: string;
  companyName: string;
  fqdn: string;
  locale: string;
  nonce: string;
  preview: boolean;
  marketingProviders: MarketingProviders;
  children: React.ReactNode;
};

export function PluginsProvider({
  companyId,
  storefrontId,
  companyName,
  fqdn,
  locale,
  nonce,
  preview,
  marketingProviders,
  children,
}: PluginsProviderProps) {
  const initialized = useRef(false);
  const { locale: paramsLocale } = useParams<{ locale?: string }>();

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    const ctx: PluginInitContext = {
      companyId,
      storefrontId,
      companyName,
      fqdn,
      locale,
      paramsLocale,
      nonce,
      preview,
      marketingProviders,
    };

    // Bridge Meta Pixel identifiers from URL params into first-party cookies
    // (and mint `_fbp` if missing) before any subsequent navigation reads them.
    // Synchronous + non-blocking — no awaiting needed.
    bridgeMetaPixelCookies();

    // Register Dash0 tenant attributes synchronously and ahead of the consent
    // gate. Dash0 is first-party observability (not marketing), and the SDK is
    // already started unconditionally in entry.client.tsx, so this changes
    // no consent posture. Doing it here — rather than behind the awaited
    // consent init below — means telemetry emitted early in the session (web
    // vitals, most errors) carries tenant context instead of shipping without
    // it while the consent gate is still pending.
    // NOTE: this still runs inside a post-hydration effect, so it does NOT
    // cover hydration-time errors (e.g. React #418), which are emitted before
    // any effect runs. Attaching tenant context to those requires registering
    // the attributes in entry.client.tsx before hydrateRoot (see US-10441).
    dash0Plugin?.init(ctx);

    amplitudePlugin?.preload();

    (async () => {
      await cookieConsentPlugin?.init(ctx);
      await Promise.allSettled([
        trackerPlugin?.init(ctx),
        amplitudePlugin?.init(ctx),
      ]);
      setPluginsReady?.();
    })();
  }, [
    companyId,
    companyName,
    fqdn,
    locale,
    paramsLocale,
    nonce,
    storefrontId,
    preview,
    marketingProviders,
  ]);

  // `PageviewTracker` is imported from a `.client` module, so it's `undefined`
  // on the server and a component on the client. Rendering it during the
  // first client render (hydration) would change the tree shape and shift
  // every downstream `React.useId()` — producing hydration mismatches on
  // any react-aria-components below. `useIsSSR` stays `true` for the initial
  // hydration pass and flips to `false` afterwards, so the server output and
  // the first client render match, and the extras mount on the next tick.
  const isSSR = useIsSSR();
  return (
    <>
      {!isSSR && (
        <>
          <PageviewTracker locale={locale} />
          <CookieConsentRouteHandler locale={locale} />
        </>
      )}
      {children}
    </>
  );
}
